/* eslint-disable no-console  */
/* eslint-disable react/sort-comp */
import React, { useState, useEffect } from 'react';
import _, { get, uniq, uniqWith, uniqBy, clone, cloneDeep, flatten } from 'lodash';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import { updateRoute } from '_Resources/Header/Actions/updateRoute';
import { getRoute } from '_Utils/routes/routeUtils';
import QcardPanel from '_Components/QcardPanel/QcardPanel';
import styled from 'styled-components';
import { HintsContent } from '_Components/QcardPanel/QcardHeader/QcardHints/Hint';
import { withTranslation } from 'react-i18next';
import { setTemporaryTriggersTable } from '_Resources/Triggers/Actions/setTemporaryTriggersTable';
import { setTriggersTableAnalysisAxis } from '_Resources/Triggers/Actions/Filters/setTriggersTableAnalysisAxis';
import {
  getAnalysisAxisOptions,
  getCalculationCriteriaOptions,
  getItemsToDisplayOptions,
  getStateItemsToDisplay,
} from '_Resources/Triggers/Services/helpers';
import { setTriggersTableItems } from '_Resources/Triggers/Actions/Filters/setTriggersTableItems';
import { setTriggersTableCalculationCriteria } from '_Resources/Triggers/Actions/Filters/setTriggersTableCalculationCriteria';
import { setTriggersTableThematics } from '_Resources/Triggers/Actions/Filters/setTriggersTableThematics';
import { setTriggersTableCriteriaKeys } from '_Resources/Triggers/Actions/Filters/setTriggersTableCriteriaKeys';
import { transformThematicsToOptions } from '_Providers/QFilterOptionsProvider/optionProviderFunctions.js';
import { useQuery, useMutation } from 'react-apollo';
import GET_THEMATICS_OVERVIEW_DATA from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicsOverviewData';
import {
  getDefaultSettings,
  formatDeep,
  getSelectedThematicsForSettings,
} from '_Cards/ThematicsCard/ThematicsOverviewCards/CategoryBreakdownCard/utils';
import GET_EMOTIONAL_TRIGGERS_TABLE from '_Resources/Triggers/Services/graphql/getEmotionalTriggersTable';
import GET_THEMATIC_SELECTION_LIST from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicSelectionList';
import SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/saveThematicsOverviewSettings';
import { setThematicSelectionList } from '_Resources/Cards/Actions/setThematicSelectionList';
import { selectThematicSelection } from '_Resources/Cards/Actions/selectThematicSelection';
import QueryTriggersNumberOfMentions from 'Routes/Insights/Triggers/QueryTriggersNumberOfMentions';
import TriggersSettingsModal from './_Components/TriggersSubHeader/_Containers/ListTriggers/_Containers/TriggersSettingsModal/TriggersSettingsModal';
import TriggersDataContainer from './TriggersDataContainer';

const StyledContainer = styled(Container)`
  width: calc(100% - 2rem);
  margin: 0 1rem;
  position: relative;
  top: 1rem;
`;

type TriggerTableType = {
  id: number;
  title: string;
  description: string;
  isDefault: boolean;
  createdBy: {
    email: string;
    firstName: string;
    lastName: string;
  };
  settings: {
    analysisAxis: string;
    calculationCriteria: string;
    itemsToDisplay: {
      name: string;
      key: string;
    };
    significanceThreshold: number;
    range: {
      startDate: string;
      endDate: string;
    };
    thematics: {
      thematic: string;
      subThematic: string;
      subSubThematic: string;
    }[];
    criteriaKeys: {
      name: string;
      values: string[];
      id: string;
      label: string;
    }[];
  };
  data: {
    color: string;
    originalColor: string;
    name: string;
    originalName: string;
    items: {
      name: string;
      originalName: string;
      percentage: number;
      value: number;
    }[];
    value: number;
    deleted: boolean;
    masked: boolean;
  }[];
  customizations: {
    color: string;
    originalColor: string;
    name: string;
    originalName: string;
    items: {
      name: string;
      originalName: string;
    }[];
    deleted: boolean;
    masked: boolean;
  }[];
};

type ThematicType = {
  thematic: string;
  subThematic: string;
  subSubThematic: string;
};

type ThematicSelectionType = {
  id: string | null;
  label: string;
  name: string;
  projectId: number;
  thematics: Array<ThematicType>;
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
};

type TriggersType = {
  t: Function;
  dispatchUpdateRoute: Function;
  dispatchSetTemporaryTriggersTable: Function;
  dispatchSetTriggersTableAnalysisAxis: Function;
  dispatchSetTriggersTableItems: Function;
  dispatchSetTriggersTableCalculationCriteria: Function;
  dispatchSetTriggersTableThematics: Function;
  dispatchSetTriggersTableCriteriaKeys: Function;
  activeTriggersTable: TriggerTableType;
  criteriaKeysOptions: {}[];
  thematicsFilters: {}[];
  projectId: number;
  range: {
    startDate: Date;
    endDate: Date;
  };
  selectedThematicSelectionOption: ThematicSelectionType;
  selectionList: Array<ThematicSelectionType>;
  dispatchSetThematicSelectionList: Function;
  dispatchSelectThematicSelection: Function;
  filterKeys: any;
};

const Triggers = ({
  t,
  dispatchUpdateRoute,
  dispatchSetTemporaryTriggersTable,
  dispatchSetTriggersTableAnalysisAxis,
  dispatchSetTriggersTableItems,
  dispatchSetTriggersTableCalculationCriteria,
  dispatchSetTriggersTableThematics,
  dispatchSetTriggersTableCriteriaKeys,
  activeTriggersTable,
  criteriaKeysOptions,
  thematicsFilters,
  projectId,
  range,
  selectedThematicSelectionOption,
  selectionList,
  dispatchSetThematicSelectionList,
  dispatchSelectThematicSelection,
  filterKeys,
}: TriggersType) => {
  const [triggersSettingsModal, setTriggersSettingsModal] = useState(false);
  const [edit, setEdit] = useState(false);
  const [settings, setSettings] = useState({});
  const [queryFilters, setQueryFilters] = useState({});
  const [currentItem, setCurrentItem] = useState(
    selectionList.find(option => option.id === selectedThematicSelectionOption?.id) || selectionList[0],
  );
  const {
    data: thematicsOverviewData,
    loading: loadingThematicsOverviewData,
    refetch: refetchThematicsOverviewData,
  } = useQuery(GET_THEMATICS_OVERVIEW_DATA, {
    variables: {
      ...queryFilters,
      projectId,
      range,
      thematicSelectionId: selectedThematicSelectionOption?.id || null,
    },
    fetchPolicy: 'cache-and-network',
  });
  const { data: dataThematicSelectionList } = useQuery(GET_THEMATIC_SELECTION_LIST, {
    fetchPolicy: 'cache-and-network',
    variables: { projectId },
  });

  const [saveThematicsOverviewSettings] = useMutation(SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION);

  const allThematics = {
    createdBy: {
      firstName: 'system',
      lastName: 'system',
      email: 'support@qemotion.com',
    },
    id: null,
    label: t('button:All thematics'),
    name: t('button:All thematics'),
    projectId,
    thematics: [],
  };

  useEffect(() => {
    getRoute(dispatchUpdateRoute, 'triggers');
  }, []);

  useEffect(() => {
    const filters = cloneDeep(filterKeys);
    filters.keywords = flatten(filterKeys.texts?.filter(t => t.name === 'keywords').map(t => t.values)) || [];
    delete filters.texts;
    setQueryFilters(filters);
  }, [filterKeys]);

  useEffect(() => {
    const initialThematics = activeTriggersTable?.settings?.thematics || [];
    const cleanInitialThematics = uniqWith(
      initialThematics.map(thematic =>
        Object.fromEntries(Object.entries(thematic).filter(([key, value]) => value != null)),
      ),
      _.isEqual,
    );
    const initialNITThematics = _.uniqWith(
      transformThematicsToOptions(cleanInitialThematics, thematicsFilters),
      _.isEqual,
    );
    dispatchSetTriggersTableThematics(initialNITThematics);

    const initialCriteriaKeys = activeTriggersTable?.settings?.criteriaKeys || [];

    const criteriaKeysCategories = uniq(initialCriteriaKeys.map(initialCriteriaKey => initialCriteriaKey.name));

    const criteriaKeysToReturn = [];
    criteriaKeysCategories.map(category => {
      initialCriteriaKeys.map(initialCriteriaKey => {
        if (category === initialCriteriaKey.name) {
          if (initialCriteriaKey.values.length === 1) {
            criteriaKeysToReturn.push({
              option: {
                id: initialCriteriaKey.values[0],
                label: initialCriteriaKey.values[0],
              },
              parents: [],
              filterCategory: initialCriteriaKey.name,
            });
          }
          if (initialCriteriaKey.values.length > 1) {
            initialCriteriaKey.values.forEach(value => {
              criteriaKeysToReturn.push({
                option: {
                  id: value,
                  label: value,
                },
                parents: [],
                filterCategory: initialCriteriaKey.name,
              });
            });
          }
        }
        return criteriaKeysToReturn;
      });
    });
    dispatchSetTriggersTableCriteriaKeys(criteriaKeysToReturn);
  }, [edit, activeTriggersTable?.settings?.thematics]);

  const categoriesHierarchies = formatDeep(
    thematicsOverviewData?.getThematicsOverviewData?.thematics,
    'thematic',
    {},
  ).map(item => {
    if (!item.subthematic) {
      return { ...item, subthematic: null, subsubthematic: null };
    }
    if (!item.subsubthematic) {
      return { ...item, subsubthematic: null };
    }
    return item;
  });

  const defaultSettings = getDefaultSettings(categoriesHierarchies);

  useEffect(() => {
    if (thematicsOverviewData) {
      setSettings(
        JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings)
          ? {
              ...defaultSettings,
              ...JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings),
              selectedThematics:
                JSON.parse(thematicsOverviewData.getThematicsOverviewData.settings).selectedThematics || formattedData,
            }
          : defaultSettings,
      );
    }
  }, [thematicsOverviewData]);

  const toggleTriggersTableSettingsModal = () => {
    if (!edit) {
      dispatchSetTemporaryTriggersTable(activeTriggersTable);
      const analysisAxisToDispatch = getAnalysisAxisOptions(t).find(
        option => option.value === activeTriggersTable.settings.analysisAxis,
      );
      dispatchSetTriggersTableAnalysisAxis(analysisAxisToDispatch);
      const itemsToDisplayVariable = activeTriggersTable.settings.itemsToDisplay;
      const itemsToDisplayArray = getItemsToDisplayOptions(criteriaKeysOptions, t);
      const stateItemsToDisplay = getStateItemsToDisplay(itemsToDisplayVariable, itemsToDisplayArray);

      dispatchSetTriggersTableItems(stateItemsToDisplay);
      const calculationCriteriaToDispatch = getCalculationCriteriaOptions(t, stateItemsToDisplay).find(
        option => option.value === activeTriggersTable.settings.calculationCriteria,
      );
      dispatchSetTriggersTableCalculationCriteria(calculationCriteriaToDispatch);

      const initialThematics = activeTriggersTable.settings.thematics;

      const cleanInitialThematics = uniqWith(
        initialThematics.map(thematic =>
          Object.fromEntries(Object.entries(thematic).filter(([key, value]) => value != null)),
        ),
        _.isEqual,
      );
      const initialNITThematics = _.uniqWith(
        transformThematicsToOptions(cleanInitialThematics, thematicsFilters),
        _.isEqual,
      );
      dispatchSetTriggersTableThematics(initialNITThematics);
      const initialCriteriaKeys = activeTriggersTable.settings.criteriaKeys;

      const criteriaKeysCategories = uniq(initialCriteriaKeys.map(initialCriteriaKey => initialCriteriaKey.name));

      const criteriaKeysToReturn = [];
      criteriaKeysCategories.map(category => {
        initialCriteriaKeys.map(initialCriteriaKey => {
          if (category === initialCriteriaKey.name) {
            if (initialCriteriaKey.values.length === 1) {
              criteriaKeysToReturn.push({
                option: {
                  id: initialCriteriaKey.values[0],
                  label: initialCriteriaKey.values[0],
                },
                parents: [],
                filterCategory: initialCriteriaKey.name,
              });
            }
            if (initialCriteriaKey.values.length > 1) {
              initialCriteriaKey.values.forEach(value => {
                criteriaKeysToReturn.push({
                  option: {
                    id: value,
                    label: value,
                  },
                  parents: [],
                  filterCategory: initialCriteriaKey.name,
                });
              });
            }
          }
          return criteriaKeysToReturn;
        });
      });
      dispatchSetTriggersTableCriteriaKeys(criteriaKeysToReturn);
    }
    setTriggersSettingsModal(!triggersSettingsModal);
    setEdit(!edit);
  };

  const getThematicsVariable = () => {
    const { settings: activeTriggersSettings } = activeTriggersTable || {};
    const { thematics } = activeTriggersSettings;
    return uniqBy(thematics, _.isEqual);
  };

  const getCriteriaKeysVariable = () => {
    const { settings: activeTriggersSettings } = activeTriggersTable;
    const { criteriaKeys } = activeTriggersSettings;
    return criteriaKeys;
  };

  const { data, title, description, settings: activeTriggersSettings, createdBy: triggersCreatedBy } =
    activeTriggersTable || {};
  const { range: activeTriggersTableRange } = activeTriggersSettings || {};
  const triggersSettingsOption = {
    text: 'Settings',
    onClick: () => {
      toggleTriggersTableSettingsModal();
    },
    icon: {
      iconType: 'INTERFACE_GEAR',
      iconColor: null,
    },
    topSeparator: false,
  };

  const onThematicSelectionChange = selectThematicSelectionOption => {
    dispatchSelectThematicSelection(selectThematicSelectionOption);
    setCurrentItem(selectThematicSelectionOption);
    const cloneSettings = cloneDeep(settings);
    cloneSettings.selectedThematics = getSelectedThematicsForSettings(selectThematicSelectionOption.thematics);
    if (selectThematicSelectionOption.id !== null) {
      cloneSettings.categoryBreakdown.onlySelectedCategories = true;
    }
    if (selectThematicSelectionOption.id === null) {
      cloneSettings.selectedThematics = [];
      cloneSettings.categoryBreakdown.onlySelectedCategories = false;
    }
    saveThematicsOverviewSettings({
      variables: {
        projectId,
        settings: JSON.stringify(cloneSettings),
        thematicSelectionId: selectThematicSelectionOption?.id || null,
      },
    });
    setSettings(cloneSettings);
  };

  useEffect(() => {
    if (dataThematicSelectionList) {
      const optionsList = dataThematicSelectionList.getThematicSelectionList.map(selection => ({
        ...selection,
        label: selection.name,
      }));

      dispatchSetThematicSelectionList(optionsList);
      const foundStoredThematicSelectionId = optionsList
        .map(option => option.id)
        .find(id => id === selectedThematicSelectionOption?.id);
      if (foundStoredThematicSelectionId) {
        setCurrentItem(optionsList[foundStoredThematicSelectionId]);
      }
      if (!foundStoredThematicSelectionId) {
        dispatchSelectThematicSelection(allThematics);
        setCurrentItem(allThematics);
        const cloneSettings = clone(settings);
        const thematicsVariableForSettings = getSelectedThematicsForSettings(allThematics?.thematics);
        cloneSettings.selectedThematics = thematicsVariableForSettings || [];
        saveThematicsOverviewSettings({
          variables: {
            projectId,
            settings: JSON.stringify(cloneSettings),
            thematicSelectionId: null,
          },
          refetchQueries: () => {
            if (!projectId || !range) return [];
            return [
              {
                query: GET_THEMATICS_OVERVIEW_DATA,
                variables: { ...filterKeys, projectId, range, thematicSelectionId: null },
              },
            ];
          },
        });
      }
    }
  }, [dataThematicSelectionList]);

  const {
    data: emotionalTriggersTableData,
    loading: loadingEmotionalTriggersTable,
    error,
    refetch: refetchEmotionalTriggersTable,
  } = useQuery(GET_EMOTIONAL_TRIGGERS_TABLE, {
    variables: {
      id: activeTriggersTable?.id,
      thematicSelectionId:
        selectedThematicSelectionOption?.id !== null ? selectedThematicSelectionOption?.id : undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  return (
    <div>
      <StyledContainer fluid className="p-0">
        {activeTriggersTable && activeTriggersTable?.id !== '' && (
          <>
            <QueryTriggersNumberOfMentions
              projectId={projectId || null}
              range={activeTriggersTableRange}
              thematics={getThematicsVariable()}
              criteriaKeys={getCriteriaKeysVariable()}
            />
            <QcardPanel
              title={title}
              displayHints
              hints={[HintsContent.HINT_TRIGGERS_ANALYSIS_AXIS, HintsContent.HINT_TRIGGERS_ITEMS_TO_DISPLAY]}
              subtitleAdditionalContent={description}
              dropDownOptions={['EXPORT', 'EXPORT_TRIGGERS_DATA']}
              specificDropDownOptions={[triggersSettingsOption]}
              triggersCreatedBy={triggersCreatedBy}
              triggersSection
              settings={settings}
              setSettings={setSettings}
              onThematicSelectionChange={onThematicSelectionChange}
              currentItem={currentItem}
              setCurrentItem={setCurrentItem}
              thematicSelectionsOptions={[allThematics, ...selectionList]}
              selectedThematicSelectionOption={selectedThematicSelectionOption}
            >
              <TriggersDataContainer data={data} />
            </QcardPanel>
          </>
        )}
      </StyledContainer>
      <TriggersSettingsModal
        title={t('Edit this triggers table')}
        subtitle={t('You can change name')}
        isOpen={triggersSettingsModal}
        toggle={toggleTriggersTableSettingsModal}
        edit={edit}
        triggersTable={activeTriggersTable}
      />
    </div>
  );
};

const mapStateToProps = state => {
  return {
    eindexUnitFahrenheit: false,
    activeTriggersTable: get(state, ['triggers', 'activeTriggersTable']),
    criteriaKeysOptions: get(state, ['filters', 'projectFilters', 'criteriaKeys', 'normal']),
    thematicsFilters: get(state, ['filters', 'projectFilters', 'thematics']),
    projectId: get(state, ['projects', 'currentProject', 'id']),
    range: get(state, ['periods', 'currentPeriod']),
    selectedThematicSelectionOption: get(state, ['thematicSelections', 'selectedThematicSelectionOption']),
    selectionList: get(state, ['thematicSelections', 'list']),
    filterKeys: get(state, ['filters', 'filterKeys']),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatchUpdateRoute: newState => {
      dispatch(updateRoute(newState));
    },
    dispatchSetTemporaryTriggersTable: newState => {
      dispatch(setTemporaryTriggersTable(newState));
    },
    dispatchSetTriggersTableAnalysisAxis: newState => {
      dispatch(setTriggersTableAnalysisAxis(newState));
    },
    dispatchSetTriggersTableItems: newState => {
      dispatch(setTriggersTableItems(newState));
    },
    dispatchSetTriggersTableCalculationCriteria: newState => {
      dispatch(setTriggersTableCalculationCriteria(newState));
    },
    dispatchSetTriggersTableThematics: newState => {
      dispatch(setTriggersTableThematics(newState));
    },
    dispatchSetTriggersTableCriteriaKeys: newState => {
      dispatch(setTriggersTableCriteriaKeys(newState));
    },
    dispatchSetThematicSelectionList: thematicSelectionList => {
      dispatch(setThematicSelectionList(thematicSelectionList));
    },
    dispatchSelectThematicSelection: thematicSelection => {
      dispatch(selectThematicSelection(thematicSelection));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('triggers', 'card')(Triggers));
